import React, { useEffect, useState } from "react";
import {
  Button,
  ControlLabel,
  FormControl,
  FormGroup,
  Grid,
  Modal,
} from "react-bootstrap";
import LeadDrawer from "./LeadDrawer";
import { CALL_STATUSES, getCallStatus } from "./statusConfig";
import { formatDate, minimumFollowUpDateTime } from "../callWorkspace.utils";
import "../callWorkspace.css";

const statusClass = (status) =>
  "cw-status cw-status-" + status.toLowerCase().replace(/\s+/g, "-");

const StatusChangeModal = ({ change, onClose, onSave, saving, error }) => {
  const [status, setStatus] = useState(change ? change.status : "");
  const [reason, setReason] = useState("");
  const [dateTime, setDateTime] = useState("");
  const [notes, setNotes] = useState("");
  const definition = status ? getCallStatus(status) : null;

  useEffect(() => {
    setStatus(change ? change.status : "");
    setReason("");
    setDateTime("");
    setNotes("");
  }, [change]);

  if (!change || !definition) return null;
  const invalidFollowUp =
    definition.requiresSchedule &&
    !!dateTime &&
    new Date(dateTime).getTime() <= Date.now();
  const canSave =
    (!definition.reasons || reason) &&
    (!definition.requiresSchedule || dateTime) &&
    !invalidFollowUp;
  return (
    <Modal show onHide={onClose} backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Set status to {definition.label}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="cw-modal-intro">
          Updating <strong>{change.lead.name}</strong> in {change.lead.batchName}.
        </p>
        <FormGroup controlId="cw-status">
          <ControlLabel>Status</ControlLabel>
          <FormControl
            componentClass="select"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setReason("");
            }}
          >
            {CALL_STATUSES.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </FormControl>
        </FormGroup>
        {definition.reasons && definition.reasons.length ? (
          <FormGroup controlId="cw-status-reason">
            <ControlLabel>{definition.reasonLabel}</ControlLabel>
            <FormControl
              componentClass="select"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            >
              <option value="">Choose an option</option>
              {definition.reasons.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </FormControl>
          </FormGroup>
        ) : null}
        {definition.requiresSchedule && (
          <FormGroup controlId="cw-follow-up-at">
            <ControlLabel>Follow-up date and time</ControlLabel>
            <FormControl
              type="datetime-local"
              value={dateTime}
              min={minimumFollowUpDateTime()}
              onChange={(event) => setDateTime(event.target.value)}
            />
            {invalidFollowUp ? (
              <span className="help-block">Select a future date and time.</span>
            ) : null}
          </FormGroup>
        )}
        <FormGroup controlId="cw-status-notes">
          <ControlLabel>
            Notes <span className="cw-optional-label">Optional</span>
          </ControlLabel>
          <FormControl
            componentClass="textarea"
            rows={3}
            value={notes}
            placeholder="Add useful context for the next caller"
            onChange={(event) => setNotes(event.target.value)}
          />
        </FormGroup>
        {error ? (
          <div className="cw-tab-error" role="alert">
            {error}
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" disabled={saving} onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          bsStyle="primary"
          disabled={!canSave || saving}
          onClick={() => {
            Promise.resolve(
              onSave({ status, reason, dateTime, notes: notes.trim() }),
            ).then((saved) => {
              if (saved) onClose();
            });
          }}
        >
          {saving ? "Saving…" : "Save status"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const CallBatchDetails = (props) => {
  const activeLabel = (props.tabs.find(
    (tab) => tab.key === props.activeTab,
  ) || {}).label || "Leads";
  return (
    <main className="cw-page">
      <Grid fluid>
        <div className="cw-header">
          <div>
            <p className="cw-kicker">CALL OPERATIONS</p>
            <h1>My Calls</h1>
            <p>Your cross-batch queue, organised by the next action.</p>
          </div>
          <div className="cw-header-summary">
            <strong>
              {props.counts.active == null ? "—" : props.counts.active}
            </strong>
            <span>calls waiting</span>
          </div>
        </div>
        <section className="cw-panel cw-queue-panel">
          <div className="cw-section-heading">
            <div>
              <h2>Lead queue</h2>
              <p>Open a lead to view the existing customer workspace.</p>
            </div>
          </div>
          <FormGroup className="cw-queue-search">
            <ControlLabel>Search leads</ControlLabel>
            <FormControl
              type="search"
              value={props.search}
              onChange={(event) => props.onSearchChange(event.target.value)}
              placeholder="Search by lead name or phone number"
              aria-label="Search leads by name or phone number"
            />
          </FormGroup>
          <div className="cw-tabs" role="tablist" aria-label="Call lead status">
            {props.tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={props.activeTab === tab.key}
                className={
                  props.activeTab === tab.key ? "cw-tab is-active" : "cw-tab"
                }
                onClick={() => props.onTabChange(tab.key)}
              >
                {tab.label}
                <span>
                  {props.counts[tab.key] == null ? "—" : props.counts[tab.key]}
                </span>
              </button>
            ))}
          </div>
          <div className="cw-leads-table-meta">
            <span>{props.pagination.total} leads</span>
          </div>
          <div
            className="cw-table-wrap"
            tabIndex="0"
            aria-label={activeLabel + " leads"}
          >
            <table className="cw-table cw-leads-table">
              <thead>
                <tr>
                  <th>Batch name</th>
                  <th>Call type</th>
                  <th>Lead name</th>
                  <th>Number</th>
                  <th>Status</th>
                  {props.activeTab === "follow_up" ? (
                    <th>Follow up at</th>
                  ) : null}
                  <th>Status reason</th>
                  <th>Attempts</th>
                  {props.activeTab !== "follow_up" ? (
                    <th>Last updated</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {props.loading ? (
                  <tr><td colSpan="8" className="cw-table-state">Loading leads…</td></tr>
                ) : props.error ? (
                  <tr><td colSpan="8" className="cw-table-state"><strong>Could not load leads</strong><span>{props.error} <Button bsStyle="link" onClick={props.onRetry}>Retry</Button></span></td></tr>
                ) : props.rows.length ? (
                  props.rows.map((lead) => (
                    <tr
                      key={lead.id}
                      className="cw-lead-row"
                      onClick={() => props.onSelectLead(lead)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ")
                          props.onSelectLead(lead);
                      }}
                      tabIndex="0"
                    >
                      <td>{lead.batchName}</td>
                      <td>{lead.callTypeName}</td>
                      <td>
                        <strong>{lead.name}</strong>
                      </td>
                      <td>{lead.phone}</td>
                      <td>
                        <div className="cw-status-cell">
                          <select
                            className={
                              statusClass(lead.status) + " cw-status-select"
                            }
                            value={lead.status}
                            aria-label={"Change status for " + lead.name}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => {
                              event.stopPropagation();
                              props.onStatusChange(lead.id, event.target.value);
                            }}
                          >
                            {CALL_STATUSES.map((status) => (
                              <option key={status.value} value={status.value}>
                                {status.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </td>
                      {props.activeTab === "follow_up" ? (
                        <td>{formatDate(lead.followUpAt, true)}</td>
                      ) : null}
                      <td>{lead.statusReason || "—"}</td>
                      <td>{lead.attempts}</td>
                      {props.activeTab !== "follow_up" ? (
                        <td>{formatDate(lead.lastUpdated, true)}</td>
                      ) : null}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="cw-table-state">
                      <strong>No leads in {activeLabel}</strong>
                      <span>This queue is clear for now.</span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="cw-pagination" aria-label="Lead pages">
            <div>
              <Button type="button" disabled={props.pagination.current_page <= 1 || props.loading} onClick={() => props.onPageChange(props.pagination.current_page - 1)}>Previous</Button>
              <span>Page {props.pagination.current_page} of {props.pagination.last_page}</span>
              <Button type="button" disabled={props.pagination.current_page >= props.pagination.last_page || props.loading} onClick={() => props.onPageChange(props.pagination.current_page + 1)}>Next</Button>
            </div>
          </div>
        </section>
      </Grid>
      <StatusChangeModal
        change={props.statusChange}
        onClose={props.onCloseStatusChange}
        onSave={props.onSaveStatus}
        saving={props.statusSaving}
        error={props.statusError}
      />
      <LeadDrawer
        key={props.selectedLead ? props.selectedLead.taskId : "lead-drawer"}
        lead={props.selectedLead}
        drawerData={props.drawerData}
        onClose={props.onCloseLead}
        onUpdateStatus={props.onOpenStatusChange}
        drawer={props.drawer}
        onToggleDetails={props.onToggleDrawerDetails}
        onSelectTab={props.onSelectDrawerTab}
        onLoadMoreActivity={props.onLoadMoreActivity}
      />
    </main>
  );
};
export default CallBatchDetails;
